import { withTranslation } from "next-i18next";
import { useState, memo } from "react";
import ahoy from "@lib/ahoy";
import { analytics } from "@lib/trackers/analytics";
import { useCurrentUser } from "@hooks/use-current-user";
import { useReloadUser } from "@hooks/use-modify-user";
import { useFiltersList } from "@hooks/use-features";
import InputButtonsSameSize from "@components/core/InputButtonsSameSize";
import Button from "@components/core/Button";
import Link from "@components/core/Link";
import shouldHideFilterSectionInListingPage from "@utils/catalog-filters/shouldHideFilterSectionInListingPage";
import { putUserFilterSizes } from "@utils/call-api/users-call";
import SectionButton from "./Elements/SectionButton";
import SectionContent from "./Elements/SectionContent";

const filterSectionKey = "sizes";

const SizesSection = memo(function SizesSection({
  sizeItems,
  refineSize,
  listingPage,
  onClose,
  filterSectionOpened,
  setFilterSectionOpened,
  t,
}) {
  const filtersList = useFiltersList();
  const user = useCurrentUser();
  const reloadUser = useReloadUser();
  const userFilterSizes = user?.size_filters;

  const [loading, setLoading] = useState(false);

  if (shouldHideFilterSectionInListingPage(listingPage, "sizes")) return null;
  if (sizeItems.length < 2) return null;

  const sectionTitle = t(`catalog:filters.section_titles.${filterSectionKey}`);

  const checkedFilters = {
    sizes: sizeItems.filter((sizeItem) => sizeItem.isRefined && sizeItem.value !== "TU").map((sizeItem) => sizeItem.value),
  }

  function handleSizeClick(optionValue) {
    const eventData = { button: "filter", filter: "sizes", value: optionValue };
    ahoy("$click", eventData);
    analytics.track("$click", eventData);

    refineSize(optionValue);
  }

  async function updateUserFilterSizes() {
    try {
      setLoading(true);

      const newUserSizes = checkedFilters.sizes;

      await putUserFilterSizes({ sizes: newUserSizes });
      await reloadUser();
      setLoading(false);
    } catch (err) {
      setLoading(false);
    }
  }

  function displayUserFilterSizes() {
    if (!user) return null;

    return (
      <div className="p-3 bg-white border-bottom">
        <div className="d-flex justify-content-between mb-3">
          <p className="mb-0">
            {t("catalog:filters.sizes.profile.title")}
          </p>
          <Link href="/profile/manage-sizes" className="link-primary no-underline">
            {userFilterSizes.length ? t("catalog:filters.sizes.profile.see_profile") : t("catalog:filters.sizes.profile.complete_profile")}
          </Link>
        </div>
        <div>
          <p className="mb-0 text-gray">
            {userFilterSizes.length ? (
              userFilterSizes.map((size) => t(`catalog:filters.sizes.${size}`)).join(", ")
            ) : (
              "- -"
            )}
          </p>
        </div>
      </div>
    );
  }

  function displaySizesFiltersOptions() {
    let sizesFiltersList = filtersList.sizes.map((filter) => {
      const sizeItem = sizeItems.find((item) => item.value === filter);

      if (!sizeItem) return;

      return ({ title: t(`catalog:filters.sizes.${filter}`), value: sizeItem.value });
    });
    sizesFiltersList = sizesFiltersList.filter((sizeFilter) => sizeFilter);

    if (!sizesFiltersList.length) return null;

    return (
      <InputButtonsSameSize
        options={sizesFiltersList}
        variant="light-gray"
        selectedValues={checkedFilters.sizes}
        onClick={(optionValue) => handleSizeClick(optionValue)}
        containerClassName="d-flex align-items-center flex-wrap gap-2"
        withTracking={false}
      />
    );
  }

  function displayDifferentSizesWarning() {
    if (!user) return null;

    const userFilterSizesOrdered = userFilterSizes.sort().join("/");
    const checkedFiltersOrdered = checkedFilters.sizes.sort().join("/");

    if (userFilterSizesOrdered === checkedFiltersOrdered) return null;

    return (
      <div className="mt-4 d-flex flex-column gap-2 justify-content-center">
        <p className="mb-0 text-center font-size-sm text-gray">
          {t("catalog:filters.sizes.profile.different_sizes_warning")}
        </p>

        <Button
          variant="link-primary"
          className="no-underline"
          size="sm"
          onClick={updateUserFilterSizes}
          loading={loading}
        >
          {t("catalog:filters.sizes.profile.update_sizes_preferences")}
        </Button>
      </div>
    );
  }

  return (
    <div>
      <SectionButton
        checkedFilters={checkedFilters}
        filterTypesToDisplay={["sizes"]}
        title={sectionTitle}
        onClick={() => setFilterSectionOpened(filterSectionKey)}
      />

      <SectionContent
        isOpen={filterSectionOpened === filterSectionKey}
        onClose={onClose}
        onGoBack={() => setFilterSectionOpened("")}
        title={sectionTitle}
      >
        {displayUserFilterSizes()}

        <div className="p-3 bg-white">
          {displaySizesFiltersOptions()}

          {displayDifferentSizesWarning()}
        </div>
      </SectionContent>
    </div>
  );
});

export default withTranslation("catalog")(SizesSection);
